import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { getProviderUsage } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/** Provider usage stats + consumers (CT-4182). Search filters consumers client-side. */
export function useProviderUsage(slug, search = '') {
  const { t } = useTranslation('admin-studio');
  const [data, setData] = useState({ stats: {}, consumers: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await getProviderUsage(slug);
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setData(validateApiResponse(res, t('common.requestFailed')).body ?? { stats: {}, consumers: [] });
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    load();
  }, [load]);

  const consumers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.consumers;
    return data.consumers.filter(
      (c) => c.name.toLowerCase().includes(q) || (c.owner ?? '').toLowerCase().includes(q),
    );
  }, [data.consumers, search]);

  return useMemo(
    () => ({ stats: data.stats, consumers, isLoading, error }),
    [data.stats, consumers, isLoading, error],
  );
}
